// TODO convert SignupFormStore setters to have no snake_case
// TODO eliminate SignupFormStore setters: use auto-constructed set** methods + an interface

/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed} from 'mobx';
import {isDevelopment, isUAT, isQA} from 'env-var-helpers';

/**************************************** PROJECT MODULES *****************************************/
import {envVars as envVarObj} from '../../setup/setup';

import {config, Serializable, storeLocally} from '@canimmunize/core-imports';
import {CityInstance} from '@canimmunize/javelinscript';

import {
    get,
    isNumberLike,
    isAlphabeticChar,
    fuzzySearch,
    getLangFromUrlPathname,
    uuid,
} from 'mad-utils/lib/react';

import {countries} from '../../components/auth-landing/auth-panel/signup/utils/get-list-data';

const lang = getLangFromUrlPathname();

/********************************************* STORE **********************************************/
/**
 * Data for the signup form flow
 * Carries data across all screens in the process
 */
@Serializable({
    filename: `signup-form-store.ts`,
    isSingleton: true,
})
export class SignupFormStore {
    public static Instance: SignupFormStore | null = null;

    //
    // MODEL PROPERTIES
    //

    @storeLocally
    @observable
    password: string;

    @storeLocally
    @observable
    confirmPassword: string;

    @storeLocally
    @observable
    name: string = ``;

    @storeLocally
    @observable
    email: string = ``;

    @storeLocally
    @observable
    postalCode: string = ``;

    @storeLocally
    @observable
    city: CityInstance = null;

    @storeLocally
    @observable
    country: string = ``;

    @storeLocally
    @observable
    spokenLanguage: string = ``;

    @storeLocally
    @observable
    doReceiveNotifications: boolean = false;

    @storeLocally
    @observable
    isLoading: boolean = false;

    @storeLocally
    @observable
    focusedInput: string = ``;

    //
    // SETTERS
    //

    public get filteredCountries(): {en: string; fr: string; code: string}[] {
        if (!this.country) return countries;
        const retList = countries.filter(countryObj => fuzzySearch(this.country, countryObj[lang]));
        return retList;
    }

    @action
    public set_password = (password: string): void => {
        this.password = password;
    };

    @action
    public set_confirmPassword = (confirmPassword: string): void => {
        this.confirmPassword = confirmPassword;
    };

    @action
    public set_name = (name: string): void => {
        this.name = name;
    };

    @action
    public set_email = (email: string): void => {
        this.email = email;
    };

    @action
    public set_postalCode = (postalCode: string): void => {
        if (
            (!postalCode[0] || isAlphabeticChar(postalCode[0])) &&
            (!postalCode[1] || isNumberLike(postalCode[1])) &&
            (!postalCode[2] || isAlphabeticChar(postalCode[2]))
        ) {
            this.postalCode = postalCode;
        }
    };

    @action
    public set_city = (city: CityInstance | null): void => {
        this.city = city;
    };

    @action
    public set_country = (country: string): void => {
        this.country = country;
    };

    @action
    public set_spokenLanguage = (spokenLanguage: string): void => {
        this.spokenLanguage = spokenLanguage;
    };

    @action
    public set_isLoading = (isLoading: boolean): void => {
        this.isLoading = isLoading;
    };

    @action
    public set_focusedInput = (focusedInput: string): void => {
        this.focusedInput = focusedInput;
    };

    @action
    public set_doReceiveNotifications = (doReceiveNotifications: boolean): void => {
        this.doReceiveNotifications = doReceiveNotifications;
    };

    //
    // CUSTOM ACTIONS
    //

    /**
     * Set in-Canada location, including validation
     * If both postal code & city are defined, discards city
     */
    @action
    public setCanadianLocation(postalCode: string, city: CityInstance) {
        if (postalCode) {
            // Validate postal code
            if (!postalCode.match(/^[a-zA-Z][0-9][a-zA-Z]$/)) {
                return new Error(`phu_valid_postal_code`);
            }
            return [
                this.set_postalCode(postalCode.toUpperCase()),
                this.set_city(null),
                this.set_country(``),
            ];
        } else if (city) {
            return [this.set_postalCode(``), this.set_city(city), this.set_country(``)];
        } else {
            return new Error(`error_location`);
        }
    }

    /**
     * Set data on exiting "create account info" page of signup form (first page)
     */
    @action
    public setCreateAccountInfo(data: {name: string; email: string; password: string}): void {
        this.set_name(data.name);
        this.set_email(data.email);
        this.set_password(data.password);
    }

    /**
     * Eliminate all data from store (should run when signup complete)
     */
    @action
    public clearSignupData(): void {
        this.set_name(``);
        this.set_email(``);
        this.set_password(``);
        this.set_confirmPassword(``);
        this.set_city(null);
        this.set_country(``);
        this.set_postalCode(``);
        this.set_spokenLanguage(``);
        this.set_doReceiveNotifications(null);
        this.set_isLoading(false);
        this.set_focusedInput(``);
    }

    //
    // INSTANTIATION
    //

    /**
     * Factory to build signup form state
     *
     * Returns SignupFormStore instance (i.e. signup form's state store)
     */
    public static new = (envVars: typeof envVarObj) => {
        SignupFormStore.Instance = SignupFormStore.Instance || new SignupFormStore(envVars);
        return SignupFormStore.Instance;
    };

    private constructor(public envVars: typeof envVarObj) {
        if (SignupFormStore.Instance) return SignupFormStore.Instance;
        this.setDevDefaults();
    }

    /**
     * In dev mode, set default passwords based on the values in the config file
     * Provided as convenience to avoid retyping each time
     */
    @action
    private setDevDefaults() {
        if (isDevelopment && !isQA && !isUAT) {
            this.name = this.envVars.DEV_FULLNAME || get(config, `appData.loginData.devName`);
            const username = get(this, `envVars.DEV_USERNAME`, ``).split(/[+@]+/g)[0];
            this.email = `${username}+${uuid.len6()}@gmail.com`;
            this.password = this.confirmPassword = this.envVars.DEV_PASSWORD;
        }
    }
}
